import { AUTOCOMPLETE_EXTRAS } from '../../config';
import { getLastTradingDay, pushQueryToHistory, turnDateToSimpleTime } from '../../services/utils';
import { IAutoCompleteItem } from '../../types/api/DashboardTypes';
import { Requested, TickData } from '../../types/api/Types';
import { SET_CURRENT_TRADING_DAY, DashboardActions, SET_DAY_RANGE, REQUEST_AUTOCOMPLETE_ITEMS_WAITING, REQUEST_AUTOCOMPLETE_ITEMS_SUCCESS, REQUEST_AUTOCOMPLETE_ITEMS_FAILURE, SET_SELECTED_TICK, SET_CHECKED_ADDITIONAL_TICK_IDS } from '../../types/redux/DashboardTypes';

export interface IDashboardState {
    currentTradingDay: string,
    dayRange: {
        fromDate: string,
        toDate: string
    },
    autoComplete: Requested<{
        bonds: IAutoCompleteItem[],
        shares: IAutoCompleteItem[],
        derivatives: IAutoCompleteItem[],
        etf: IAutoCompleteItem[]
    }>,
    // Selected Tick explanation.
    // 1. clicking on a row, data point, etc calls the setSelectedTick function
    // selectedTick is used for tick comparison to color the correct row
    // When selectedTick changes, change the hash of the url
    // Changing the url should auto scroll.
    // But also:
    // On render, if hash exists and selectedTick = null, overwrite it.
    selectedTick: TickData | null,
    // selectedTickTableRow: HTMLTableRowElement | null
    checkedAdditionalTimestamps: string[];
    checkedAdditionalTickIds: string[];
}

const getInitialState = (): IDashboardState => {
    const lastSimpleTradingDay = getLastTradingDay();
    return {
        currentTradingDay: lastSimpleTradingDay,
        dayRange: {
            fromDate: lastSimpleTradingDay,
            toDate: lastSimpleTradingDay
        },
        autoComplete: undefined,
        selectedTick: null,
        // selectedTickTableRow: null
        checkedAdditionalTimestamps: [],
        checkedAdditionalTickIds: [],
    }
}

const initialState: IDashboardState = getInitialState();

export default function(state: IDashboardState = initialState, action: DashboardActions): IDashboardState {
    switch(action.type) {
        case SET_CURRENT_TRADING_DAY:
        {
            const { newTradingDay, skipPushingToHistory } = action.payload;
            const simpleDateStringNewTradingDay = turnDateToSimpleTime(new Date(newTradingDay));

            // Test for same day. Cancel action.
            if (simpleDateStringNewTradingDay === state.currentTradingDay) return state;

            if (!skipPushingToHistory) {
                pushQueryToHistory('tradingDay', simpleDateStringNewTradingDay);
            }

            const newState: IDashboardState = {
                ...state,
                currentTradingDay: simpleDateStringNewTradingDay
            };
            return newState;
        }
        case SET_DAY_RANGE:
        {
            const { fromDate, toDate, skipPushingToHistory } = action.payload;
            const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
            const simpleToDate = turnDateToSimpleTime(new Date(toDate));

            const newDayRange = {
                fromDate: simpleFromDate,
                toDate: simpleToDate
            };

            if (!skipPushingToHistory) {
                pushQueryToHistory('fromDate', simpleFromDate);
                pushQueryToHistory('toDate', simpleToDate, true);
            }

            const newState: IDashboardState = {
                ...state,
                dayRange: newDayRange
            }
            return newState;
        }
        case REQUEST_AUTOCOMPLETE_ITEMS_WAITING: {
            const newState: IDashboardState = {
                ...state,
                autoComplete: 'waiting'
            };
            return newState;
        }
        case REQUEST_AUTOCOMPLETE_ITEMS_SUCCESS: {
            const { autoCompleteItems } = action;
            const autoCompleteBonds: IAutoCompleteItem[] = [];
            const autoCompleteShares: IAutoCompleteItem[] = [];
            const autoCompleteDerivatives: IAutoCompleteItem[] = [];
            const autoCompleteEtfs: IAutoCompleteItem[] = [];
            for (const bond of AUTOCOMPLETE_EXTRAS.bonds) {
                autoCompleteBonds.push({ name: bond[1], symbol: bond[0], tickerType: 'bond', isActive: 'T' });
            }
            for (const share of AUTOCOMPLETE_EXTRAS.shares) {
                autoCompleteShares.push({ name: share[1], symbol: share[0], tickerType: 'share', isActive: 'T' });
            }
            for (let autoCompleteItem of autoCompleteItems) {
                // TEMP FIX
                // if (autoCompleteItem.IsActive === 'T') {
                    if (autoCompleteItem.tickerType === 'bond') {
                        autoCompleteBonds.push(autoCompleteItem)
                    } else if (autoCompleteItem.tickerType === 'share') {
                        autoCompleteShares.push(autoCompleteItem)
                    } else if (autoCompleteItem.tickerType === 'derivative') {
                        autoCompleteDerivatives.push(autoCompleteItem);
                    } else if (autoCompleteItem.tickerType === 'exchangetradedfund') {
                        autoCompleteEtfs.push(autoCompleteItem);
                    }
                // }
            }
            const newState: IDashboardState = {
                ...state,
                autoComplete: {
                    shares: autoCompleteShares,
                    bonds: autoCompleteBonds,
                    derivatives: autoCompleteDerivatives,
                    etf: autoCompleteEtfs
                }
            }
            return newState;
        }
        case REQUEST_AUTOCOMPLETE_ITEMS_FAILURE: {
            const { error } = action;
            const newState: IDashboardState = {
                ...state,
                autoComplete: { message: error || 'Unknown error'},
            };
            return newState;
        }
        case SET_SELECTED_TICK: {
            const selectedTick = action.payload;
            const newState: IDashboardState = {
                ...state,
                selectedTick
            };
            return newState;
        }
        case SET_CHECKED_ADDITIONAL_TICK_IDS: {
            const { checkedAdditionalTickIds } = action.payload;
            return {
                ...state,
                checkedAdditionalTickIds
            };
        }
        default:
        {
            return state;
        }
    }
}
